import React from "react";
import { withRouter } from "react-router-dom";
import SelectSearch from "react-select-search/dist/cjs";
import moment from "moment";
import Swal from "sweetalert2";
import ProgressBar from "@ramonak/react-progress-bar";

import {
	getReportResellers,
	getMerchants,
	getAccessControl,
	getReportToken,
	getReportList,
} from "../../libs/functions";

import { io } from "socket.io-client";
import ss from "socket.io-stream";
import { saveAs } from "file-saver";

import Container from "../../components/Container";
import DateRange from "./components/DateRange";
import withSSR from "../../components/withSSR";
import DataTable from "../../components/DataTable";

class Reports extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			resellers: [],
			merchants: [],
			viewingRange: {
				start: moment().startOf("month"),
				end: moment(),
				maxDate: moment(),
			},
			mode: "Active",
			isLoading: true,
			master: false,
			selectedMerchant: null,
			selectedReseller: null,
			reportConfig: null,
			reportList: [],
			allReportGenerated: false,
		};
	}

	componentDidMount = async () => {
		this.setState({
			master: (await getAccessControl()) === "master",
			reportConfig: await getReportToken(),
		});
		this.retrieveResellers();
		// this.retrieveMerchants();
	};

	retrieveMerchants = async (reseller) => {
		this.setState({ isLoading: true, merchants: [] });

		let data = await this.props.fetchData(this.container.loader, getMerchants, {
			isArchive: this.state.mode !== "Active",
		});

		if (reseller)
			data = data.filter((m) => String(m.reseller) === String(reseller));

		if (data.length > 0) {
			let merchants = data.map((merchant) => {
				return { name: merchant.name, value: merchant.id };
			});
			merchants.unshift({ name: "All", value: "all" });

			this.setState({ merchants });
		}
	};

	retrieveResellers = async () => {
		this.setState({ isLoading: true, resellers: [] });

		const data = await this.props.fetchData(
			this.container.loader,
			getReportResellers,
			{
				isArchive: this.state.mode !== "Active",
			}
		);

		let resellers = data.map((reseller) => {
			return { name: reseller.name, value: reseller.id };
		});
		resellers.unshift({ name: "All", value: "all" });

		this.setState({ resellers });
	};

	selectReseller = (value) => {
		this.setState({ selectedReseller: value });
		this.retrieveMerchants(value === "all" ? undefined : value);
	};

	selectMerchant = (value) => {
		this.setState({ selectedMerchant: value });
	};

	dateChange = (dates) => {
		const [start, end] = dates;
		const current = { ...this.state.viewingRange };

		if (start) current.start = moment(start);
		else current.start = null;
		if (end) current.end = moment(end);
		else current.end = null;

		if (current.start) {
			const newMaxDate = current.start.clone().add(1, "months");
			if (newMaxDate.isBefore(new Date())) current.maxDate = newMaxDate;
		}

		this.setState({ viewingRange: current });
	};

	executeGetReportList = async (payload) => {
		const {
			merchants,
			selectedMerchant,
			viewingRange: { start, end },
			master,
		} = payload;

		const { reportList } = await getReportList({
			merchant:
				selectedMerchant === "all"
					? merchants.map((m) => m.value).filter((m) => m !== "all")
					: [selectedMerchant],
			startDate: start.startOf("day").toISOString(),
			endDate: end.endOf("day").toISOString(),
			isMaster: master,
		});

		for (let i of reportList) {
			this.setState({
				[`dl_${i.merchant._id}`]: { status: "Ready", progress: 0 },
			});
		}

		this.setState({ reportList });
	};

	getReportList = async () => {
		if (this.state.selectedMerchant === null) return;
		Swal.fire({
			title: "Searching Report",
			onBeforeOpen: () => {
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					await this.executeGetReportList(this.state);
					Swal.close();
				} catch (err) {
					Swal.fire({
						icon: "error",
						title: "Unable to retrieve data",
						text: err,
						onClose: () => {},
					});
				}
			},
		});
	};

	executeGenerateReport = (socket, payload) => {
		return new Promise((resolve, reject) => {
			const stream = ss.createStream();
			const { merchantId } = payload;
			let fileBuffer = [];
			let fileLength = 0;
			let fileName = "report.xlsx";

			const updateStatus = (merchantId, status, progress, file = null) => {
				this.setState({
					[`dl_${merchantId}`]: { status, progress, file },
				});
			};

			ss(socket).emit("report", stream, payload, (error, response) => {
				fileName = response.fileName;

				stream.on("data", function (chunk) {
					fileLength += chunk.length;
					let progress = Math.floor((fileLength / response.size) * 100);
					progress = Math.max(progress - 2, 1);
					fileBuffer.push(chunk);
					updateStatus(merchantId, "Generating Report", progress);
				});

				stream.on("end", function () {
					const filedata = new Uint8Array(fileLength);
					let i = 0;

					//== Loop to fill the final array
					fileBuffer.forEach(function (buff) {
						for (var j = 0; j < buff.length; j++) {
							filedata[i] = buff[j];
							i++;
						}
					});

					const file = new Blob([filedata], {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					});
					updateStatus(merchantId, "Report Generated", 100, {
						file,
						fileName,
					});

					resolve(true);
				});
			});
		});
	};

	generateReport = async (socket, payload) => {
		const { merchantId } = payload;
		const reportStateKey = `dl_${merchantId}`;

		this.setState({
			[reportStateKey]: { status: "Generating Report", progress: 0 },
		});

		await this.executeGenerateReport(socket, payload);
	};

	generateSingleReport = async (merchantId) => {
		const {
			viewingRange: { start, end },
		} = this.state;
		const socket = io(process.env.RAZZLE_REPORT_URL);
		await this.generateReport(socket, {
			merchantId,
			startDate: start.startOf("day").toISOString(),
			endDate: end.endOf("day").toISOString(),
		});
		socket.emit("reportGenerated", true);
		socket.disconnect();
	};

	generateAllReport = async () => {
		const {
			reportList,
			viewingRange: { start, end },
		} = this.state;

		const socket = io(process.env.RAZZLE_REPORT_URL);

		for (let i of reportList) {
			await this.generateReport(socket, {
				merchantId: i.merchant._id,
				startDate: start.startOf("day").toISOString(),
				endDate: end.endOf("day").toISOString(),
			});
		}
		socket.emit("reportGenerated", true);
		socket.disconnect();
	};

	downloadGenerated = () => {
		const {
			reportList,
			viewingRange: { start, end },
		} = this.state;
		let generatedFiles = {};
		for (let i of reportList) {
			const reportState = this.state[`dl_${i.merchant._id}`];
			if (reportState.progress === 100) {
				if (!generatedFiles[i.merchant.reseller])
					generatedFiles[i.merchant.reseller] = [];
				generatedFiles[i.merchant.reseller].push(reportState.file);
			}
		}

		if (Object.keys(generatedFiles).length > 0) {
			const zip = require("jszip")();
			for (let [reseller, files] of Object.entries(generatedFiles)) {
				const folder = zip.folder(reseller);
				for (let file of files) {
					folder.file(file.fileName, file.file);
				}
			}
			zip.generateAsync({ type: "blob" }).then((content) => {
				saveAs(
					content,
					`${start.format("YYYY-MM-DD")}_${end.format(
						"YYYY-MM-DD"
					)}_commission_report.zip`
				);
			});
		}
	};

	render() {
		const { identifier, MDRTypes } = this.props;
		const {
			resellers,
			merchants,
			selectedReseller,
			selectedMerchant,
			viewingRange: { start, end, maxDate },
			reportList,
		} = this.state;

		return (
			<Container ref={(c) => (this.container = c)} activeMenu={identifier}>
				<div>
					<div className="uk-flex uk-flex-row uk-flex-middle uk-margin-bottom">
						<h1 className="content-header uk-margin-right uk-margin-remove-bottom">
							{identifier}
						</h1>
					</div>
					{resellers.length > 0 && (
						<div className="uk-card uk-card-small uk-card-body uk-card-default">
							<div className="uk-margin uk-flex uk-flex-row">
								<div className="uk-margin-right">
									<SelectSearch
										options={resellers}
										search
										placeholder="Select Reseller"
										onChange={this.selectReseller}
									/>
								</div>
								{selectedReseller && merchants.length > 0 && (
									<div className="uk-margin-right">
										<SelectSearch
											options={merchants}
											search
											placeholder="Select Merchant"
											onChange={this.selectMerchant}
										/>
									</div>
								)}
							</div>
							<div className="uk-margin uk-flex uk-flex-row">
								{selectedMerchant && (
									<DateRange
										startDate={start && start.toDate()}
										endDate={end && end.toDate()}
										maxDate={maxDate.toDate()}
										dateChange={this.dateChange}
										searchReport={this.getReportList}
									/>
								)}
								{reportList.length > 0 && (
									<div className="uk-flex uk-flex-middle">
										<button
											className="uk-button uk-button-small uk-button-primary uk-margin-left action-btn"
											type="button"
											onClick={() => this.generateAllReport()}
										>
											<span className="uk-text-middle uk-text-bold">
												Generate All
											</span>
										</button>
										<button
											className="uk-button uk-button-small uk-button-primary uk-margin-left action-btn"
											type="button"
											onClick={() => this.downloadGenerated()}
										>
											<span className="uk-text-middle uk-text-bold">
												Download Generated
											</span>
										</button>
									</div>
								)}
							</div>
							{reportList.length > 0 && (
								<DataTable
									className="uk-table uk-table-hover uk-table-striped uk-table-middle uk-table-responsive sales-channel-table uk-text-small"
									options={{
										paging: false,
									}}
								>
									<thead>
										<tr>
											<th>Merchant</th>
											<th>Transaction Count</th>
											<th>Report Status</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>
										{reportList.map((r, index) => {
											const reportState = this.state[`dl_${r.merchant._id}`];
											return (
												<tr key={index}>
													<td>{r.merchant.name}</td>
													<td>{r.count}</td>
													<td>
														{reportState.status === "Ready" ? (
															<button
																className="uk-button uk-button-small uk-button-primary action-btn"
																type="button"
																onClick={() =>
																	this.generateSingleReport(r.merchant._id)
																}
															>
																<span className="uk-text-middle uk-text-bold">
																	Generate
																</span>
															</button>
														) : (
															<span className="uk-text-bold">
																{reportState.status}
															</span>
														)}
														{reportState.progress > 0 && (
															<ProgressBar
																bgColor="#da4c22"
																completed={reportState.progress}
															/>
														)}
													</td>
													<td>
														<button
															className="uk-button uk-button-small uk-button-primary action-btn"
															type="button"
															onClick={() =>
																saveAs(
																	reportState.file.file,
																	reportState.file.fileName
																)
															}
															disabled={reportState.progress !== 100}
														>
															<span className="uk-text-middle uk-text-bold">
																Download
															</span>
														</button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</DataTable>
							)}
						</div>
					)}
				</div>
			</Container>
		);
	}
}

export default withSSR(withRouter(Reports));
